import { memo, ReactElement, useMemo, useCallback } from "react"
import styled from "styled-components"
import Heading from "components/Typography/Heading"
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts"
import VariationDisplay from "../VariationDisplay"
import generateRandomColor from "utils/generateRandomColor"
import { useDeepMemo } from "utils/useDeepMemo"
import { Colors } from "themes/palette"

const eventTypeMap: Record<string, string> = {
  "Eventos com sucesso": "Eventos com sucesso",
  "Eventos com falha": "Eventos com falha"
}

export const Title = styled.span`
  display: flex;
  align-items: center;
  padding: 40px 0px 24px 0px;
`

const ChartContainer = styled.div`
  height: 340px;
  width: 100%;
  background-color: #fafafa;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 8px;
  padding-left: 36px;
`

const TotalContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

interface EventsBreakdownData {
  total: number
  date: string
  breakdown?: Record<string, number>
}

interface DailyResultsItem {
  date: string
  total?: number
  totalEvents?: number
  breakdown?: Record<string, number>
}

interface EventsBreakdownChartProps {
  title: string
  chartData: EventsBreakdownData[] | App.ChartData
}

const EventsBreakdownChart = (props: EventsBreakdownChartProps): ReactElement => {
  const { chartData, title } = props

  const normalizedData = useMemo((): EventsBreakdownData[] => {
    if (Array.isArray(chartData)) {
      return chartData
    }

    if (chartData && chartData.dailyResults) {
      const result = chartData.dailyResults.map((item: DailyResultsItem) => ({
        date: item.date,
        total: item.total || item.totalEvents || 0,
        breakdown: item.breakdown
      }))

      const allKeys = new Set<string>()

      result.forEach((item) => {
        if (item.breakdown) {
          Object.keys(item.breakdown).forEach((key) => allKeys.add(key))
        }
      })

      result.forEach((item) => {
        if (!item.breakdown) {
          item.breakdown = {}
        }
        allKeys.forEach((key) => {
          if (!(key in item.breakdown!)) {
            item.breakdown![key] = 0
          }
        })
      })

      return result
    }

    return []
  }, [chartData])

  const transformedData = useMemo(() => {
    if (!normalizedData || normalizedData.length === 0) return []

    const result = normalizedData.map((item) => {
      const baseData: Record<string, number | string> = {
        date: item.date,
        total: item.total
      }

      if (item.breakdown) {
        Object.keys(item.breakdown).forEach((key) => {
          if (item.breakdown) {
            baseData[key] = item.breakdown[key]
          }
        })
      }

      return baseData
    })

    return result
  }, [normalizedData])

  const breakdownKeys = useMemo(() => {
    const keys = new Set<string>()

    normalizedData.forEach((item) => {
      if (item.breakdown) {
        Object.keys(item.breakdown).forEach((key) => keys.add(key))
      }
    })

    const result = Array.from(keys)

    return result
  }, [normalizedData])

  const colorMap = useDeepMemo(() => {
    const map: Record<string, string> = {}

    breakdownKeys.forEach((key) => {
      if (key === "Eventos com sucesso") {
        map[key] = Colors.success
      } else if (key === "Eventos com falha") {
        map[key] = Colors.error
      } else {
        map[key] = generateRandomColor()
      }
    })

    return map
  }, [breakdownKeys])

  const summary = useMemo(() => {
    if (!Array.isArray(chartData) && chartData && chartData.summary) {
      return chartData.summary
    }

    const total = normalizedData.reduce((sum, item) => sum + item.total, 0)

    let variation = 0

    if (normalizedData.length > 1) {
      const firstPeriod = normalizedData[0]?.total || 0
      const lastPeriod = normalizedData[normalizedData.length - 1]?.total || 0

      if (firstPeriod > 0) {
        variation = ((lastPeriod - firstPeriod) / firstPeriod) * 100
      }
    }

    return {
      total,
      variation,
      difference: variation
    }
  }, [chartData, normalizedData])

  const tooltipFormatter = useCallback((value: number | string, name: string) => {
    return [value, name] as [number | string, string]
  }, [])

  const tooltipLabelFormatter = useCallback((label: string) => {
    return "Data: " + label
  }, [])

  return (
    <ChartContainer>
      <HeaderContainer>
        <Heading variant="h6">{title}</Heading>
        <TotalContainer>
          <Heading variant="h5">{summary.total}</Heading>
          <VariationDisplay value={summary} />
        </TotalContainer>
      </HeaderContainer>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart width={500} height={400} data={transformedData} margin={{ top: 16, bottom: 24, right: 4 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip
            formatter={tooltipFormatter}
            labelFormatter={tooltipLabelFormatter}
            wrapperStyle={{
              zIndex: 9999
            }}
          />
          <Legend wrapperStyle={{ bottom: -10 }} />
          {breakdownKeys.length > 0 ? (
            breakdownKeys.map((key) => (
              <Area
                key={key}
                type="linear"
                dataKey={key}
                name={eventTypeMap[key] || key}
                stroke={colorMap[key]}
                fill={colorMap[key]}
                fillOpacity={0.6}
              />
            ))
          ) : (
            <Area
              key="total"
              type="linear"
              dataKey="total"
              name="Total de Eventos"
              stroke="#6062CB"
              fill="#6062CB"
              fillOpacity={0.6}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

export default memo(EventsBreakdownChart)
